import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Typography,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import FileDropzone from './import/FileDropzone';
import ImportProgress from './import/ImportProgress';
import MovieMatchCard from './import/MovieMatchCard';
import api from '../services/api';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
`;

const StepContainer = styled(Card)`
  margin-bottom: 30px;
`;

const StepHeader = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 15px 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
`;

const PreviewList = styled(List)`
  max-height: 200px;
  overflow-y: auto;
  background-color: #f9f9f9;
  border-radius: 4px;
`;

const SummaryCard = styled.div`
  background-color: #e8f5e9;
  border-left: 4px solid #4caf50;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const ErrorList = styled.div`
  background-color: #ffebee;
  border-left: 4px solid #f44336;
  padding: 15px;
  border-radius: 4px;
  margin-top: 15px;
`;

function LetterboxdImport({ user }) {
  const navigate = useNavigate();

  // State management
  const [step, setStep] = useState('upload'); // upload, preview, matching, manual, importing, complete
  const [zipFile, setZipFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [options, setOptions] = useState({
    importRatings: true,
    importWatchlist: true,
    overwriteExisting: false
  });
  const [matchedMovies, setMatchedMovies] = useState([]);
  const [unmatchedMovies, setUnmatchedMovies] = useState([]);
  const [manualMatches, setManualMatches] = useState({});
  const [importSummary, setImportSummary] = useState(null);
  const [error, setError] = useState(null);

  // Step 1: File Upload
  const handleFileSelect = (file) => {
    setZipFile(file);
    setError(null);
  };

  const handleUploadAndValidate = async () => {
    if (!zipFile) return;

    setError(null);
    setStep('validating');

    try {
      const result = await api.uploadLetterboxdZip(zipFile);
      setPreviewData(result);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to process ZIP file');
      setStep('upload');
    }
  };

  // Step 2: Preview and Options
  const handleStartImport = async () => {
    setError(null);

    // Combine movies from selected sources
    const movies = [];

    if (options.importRatings && previewData.ratings) {
      // Use full data, not just preview
      movies.push(...previewData.ratings.data.map(m => ({ ...m, type: 'rating' })));
    }

    if (options.importWatchlist && previewData.watchlist) {
      // Use full data, not just preview
      movies.push(...previewData.watchlist.data.map(m => ({ ...m, type: 'watchlist' })));
    }

    if (movies.length === 0) {
      setError('Please select at least one import option');
      return;
    }

    setStep('matching');

    // Start auto-matching in chunks to avoid timeout
    try {
      const allMatched = [];
      const allUnmatched = [];
      const chunkSize = 25; // Reduced from 50 to avoid timeouts
      let offset = 0;

      while (offset < movies.length) {
        console.log(`Matching chunk ${offset + 1}-${Math.min(offset + chunkSize, movies.length)} of ${movies.length}`);

        const chunkResults = await api.matchLetterboxdMovies(movies, user.id, offset, chunkSize);

        allMatched.push(...chunkResults.matched);
        allUnmatched.push(...chunkResults.unmatched);

        offset += chunkSize;

        // Short delay between chunks
        if (offset < movies.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setMatchedMovies(allMatched);
      setUnmatchedMovies(allUnmatched);

      if (allUnmatched.length > 0) {
        setStep('manual');
      } else {
        // No unmatched movies, proceed to import
        await executeImport(allMatched, []);
      }
    } catch (err) {
      setError(err.message || 'Failed to match movies');
      setStep('preview');
    }
  };

  // Step 3: Manual Matching
  const handleManualMatch = (movie, tmdbData) => {
    setManualMatches({
      ...manualMatches,
      [`${movie.title}-${movie.year}`]: {
        ...movie,
        ...tmdbData
      }
    });

    // Remove from unmatched
    setUnmatchedMovies(unmatchedMovies.filter(
      m => !(m.title === movie.title && m.year === movie.year)
    ));
  };

  const handleSkipMovie = (movie) => {
    // Just remove from unmatched
    setUnmatchedMovies(unmatchedMovies.filter(
      m => !(m.title === movie.title && m.year === movie.year)
    ));
  };

  const handleCompleteManualMatching = async () => {
    // Combine matched movies with manual matches
    const allMatchedMovies = [
      ...matchedMovies,
      ...Object.values(manualMatches)
    ];

    console.log(`[Letterboxd] Proceeding with ${allMatchedMovies.length} matched movies`);
    console.log(`[Letterboxd] Skipping ${unmatchedMovies.length} unmatched movies`);
    await executeImport(allMatchedMovies, []);
  };

  const handleSkipAllUnmatched = async () => {
    // Import only the already matched movies, skip all unmatched
    console.log(`[Letterboxd] Skipping all ${unmatchedMovies.length} unmatched movies`);
    console.log(`[Letterboxd] Importing ${matchedMovies.length} matched movies`);
    await executeImport(matchedMovies, []);
  };

  // Step 4: Execute Import
  const executeImport = async (matched, unmatched) => {
    setStep('importing');
    setError(null);

    try {
      const ratingsToImport = matched.filter(m => m.type === 'rating');
      const watchlistToImport = matched.filter(m => m.type === 'watchlist');

      console.log(`[Letterboxd] Executing import:`);
      console.log(`  - Total matched: ${matched.length}`);
      console.log(`  - Ratings: ${ratingsToImport.length}`);
      console.log(`  - Watchlist: ${watchlistToImport.length}`);

      // Import in chunks to avoid timeout (max 50 per request)
      const chunkSize = 50;
      const totalImportSummary = {
        imported: { ratings: 0, watchlist: 0 },
        skipped: 0,
        errors: []
      };

      // Import ratings in chunks
      for (let i = 0; i < ratingsToImport.length; i += chunkSize) {
        const chunk = ratingsToImport.slice(i, i + chunkSize);
        console.log(`[Letterboxd] Importing ratings chunk ${i + 1}-${i + chunk.length} of ${ratingsToImport.length}`);

        const result = await api.executeLetterboxdImport({
          userId: user.id,
          ratings: chunk,
          watchlist: [], // Import ratings first
          options: { overwriteExisting: options.overwriteExisting }
        });

        totalImportSummary.imported.ratings += result.imported.ratings;
        totalImportSummary.skipped += result.skipped;
        totalImportSummary.errors.push(...result.errors);

        // Short delay between chunks
        if (i + chunkSize < ratingsToImport.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Import watchlist in chunks
      for (let i = 0; i < watchlistToImport.length; i += chunkSize) {
        const chunk = watchlistToImport.slice(i, i + chunkSize);
        console.log(`[Letterboxd] Importing watchlist chunk ${i + 1}-${i + chunk.length} of ${watchlistToImport.length}`);

        const result = await api.executeLetterboxdImport({
          userId: user.id,
          ratings: [], // Already imported ratings
          watchlist: chunk,
          options: { overwriteExisting: options.overwriteExisting }
        });

        totalImportSummary.imported.watchlist += result.imported.watchlist;
        totalImportSummary.skipped += result.skipped;
        totalImportSummary.errors.push(...result.errors);

        // Short delay between chunks
        if (i + chunkSize < watchlistToImport.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      console.log(`[Letterboxd] Import complete! Imported ${totalImportSummary.imported.ratings} ratings, ${totalImportSummary.imported.watchlist} watchlist`);
      setImportSummary(totalImportSummary);
      setStep('complete');
    } catch (err) {
      setError(err.message || 'Failed to import data');
      setStep('manual');
    }
  };

  // Render different steps
  const renderUploadStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Step 1: Upload Letterboxd Export</Typography>
      </StepHeader>
      <CardContent>
        <Typography variant="body1" gutterBottom>
          Export your data from Letterboxd and upload the ZIP file here.
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '20px' }}>
          Your ZIP should contain ratings.csv and/or watchlist.csv files.
        </Typography>

        {error && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        <FileDropzone onFileSelect={handleFileSelect} selectedFile={zipFile} />

        <ButtonGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUploadAndValidate}
            disabled={!zipFile || step === 'validating'}
          >
            {step === 'validating' ? 'Processing...' : 'Continue'}
          </Button>
        </ButtonGroup>
      </CardContent>
    </StepContainer>
  );

  const renderPreviewStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Step 2: Preview & Options</Typography>
      </StepHeader>
      <CardContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          Found in your export:
        </Typography>

        {previewData.ratings && (
          <div style={{ marginBottom: '20px' }}>
            <Typography variant="subtitle1">
              Ratings: {previewData.ratings.count} movies
            </Typography>
            <PreviewList>
              {previewData.ratings.preview.map((movie, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${movie.title} (${movie.year})`}
                    secondary={`Rating: ${movie.rating} stars`}
                  />
                </ListItem>
              ))}
            </PreviewList>
          </div>
        )}

        {previewData.watchlist && (
          <div style={{ marginBottom: '20px' }}>
            <Typography variant="subtitle1">
              Watchlist: {previewData.watchlist.count} movies
            </Typography>
            <PreviewList>
              {previewData.watchlist.preview.map((movie, index) => (
                <ListItem key={index}>
                  <ListItemText primary={`${movie.title} (${movie.year})`} />
                </ListItem>
              ))}
            </PreviewList>
          </div>
        )}

        <Divider style={{ margin: '20px 0' }} />

        <Typography variant="h6" gutterBottom>
          Import Options:
        </Typography>

        <FormGroup>
          {previewData.ratings && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.importRatings}
                  onChange={(e) => setOptions({ ...options, importRatings: e.target.checked })}
                />
              }
              label="Import ratings"
            />
          )}
          {previewData.watchlist && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.importWatchlist}
                  onChange={(e) => setOptions({ ...options, importWatchlist: e.target.checked })}
                />
              }
              label="Import watchlist"
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={options.overwriteExisting}
                onChange={(e) => setOptions({ ...options, overwriteExisting: e.target.checked })}
              />
            }
            label="Overwrite existing ratings"
          />
        </FormGroup>

        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '10px' }}>
          Note: Movies already rated in Kritics will be skipped unless "Overwrite" is checked.
          Watchlist items are always skipped if you've already rated the movie.
        </Typography>

        <ButtonGroup>
          <Button
            variant="outlined"
            onClick={() => setStep('upload')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStartImport}
          >
            Start Import
          </Button>
        </ButtonGroup>
      </CardContent>
    </StepContainer>
  );

  const renderMatchingStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Step 3: Matching Movies</Typography>
      </StepHeader>
      <CardContent>
        <ImportProgress
          message="Matching movies to TMDB database..."
          current={0}
          total={0}
          indeterminate={true}
        />
      </CardContent>
    </StepContainer>
  );

  const renderManualMatchingStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Step 4: Manual Matching</Typography>
      </StepHeader>
      <CardContent>
        <Alert severity="info" style={{ marginBottom: '20px' }}>
          We couldn't automatically match {unmatchedMovies.length} movie(s).
          Please search and select the correct match, or skip.
        </Alert>

        {unmatchedMovies.map((movie, index) => (
          <MovieMatchCard
            key={index}
            movie={movie}
            onMatch={handleManualMatch}
            onSkip={handleSkipMovie}
          />
        ))}

        <ButtonGroup>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSkipAllUnmatched}
          >
            Skip All & Import ({matchedMovies.length} movies)
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCompleteManualMatching}
            disabled={unmatchedMovies.length > 0}
          >
            {unmatchedMovies.length > 0
              ? `${unmatchedMovies.length} movies remaining`
              : 'Complete Import'}
          </Button>
        </ButtonGroup>
      </CardContent>
    </StepContainer>
  );

  const renderImportingStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Step 5: Importing</Typography>
      </StepHeader>
      <CardContent>
        <ImportProgress
          message="Importing your data..."
          current={0}
          total={0}
          indeterminate={true}
        />
      </CardContent>
    </StepContainer>
  );

  const renderCompleteStep = () => (
    <StepContainer>
      <StepHeader>
        <Typography variant="h5">Import Complete!</Typography>
      </StepHeader>
      <CardContent>
        <SummaryCard>
          <Typography variant="h6" gutterBottom>
            <CheckCircle style={{ verticalAlign: 'middle', marginRight: '10px', color: '#4caf50' }} />
            Successfully Imported
          </Typography>
          <Typography variant="body1">
            Ratings: {importSummary?.imported?.ratings || 0}
          </Typography>
          <Typography variant="body1">
            Watchlist: {importSummary?.imported?.watchlist || 0}
          </Typography>
          {importSummary?.skipped > 0 && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Skipped {importSummary.skipped} movie(s) (already existed)
            </Typography>
          )}
        </SummaryCard>

        {importSummary?.errors && importSummary.errors.length > 0 && (
          <ErrorList>
            <Typography variant="h6" gutterBottom>
              <ErrorIcon style={{ verticalAlign: 'middle', marginRight: '10px' }} />
              Errors
            </Typography>
            {importSummary.errors.map((error, index) => (
              <Typography key={index} variant="body2">
                {error.title} ({error.year}): {error.error}
              </Typography>
            ))}
          </ErrorList>
        )}

        <ButtonGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/profile')}
          >
            Go to Profile
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setStep('upload');
              setZipFile(null);
              setPreviewData(null);
              setMatchedMovies([]);
              setUnmatchedMovies([]);
              setManualMatches({});
              setImportSummary(null);
              setError(null);
            }}
          >
            Import Another File
          </Button>
        </ButtonGroup>
      </CardContent>
    </StepContainer>
  );

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Import from Letterboxd
      </Typography>
      <Typography variant="body1" color="textSecondary" style={{ marginBottom: '30px' }}>
        Import your ratings and watchlist from your Letterboxd export.
      </Typography>

      {step === 'upload' && renderUploadStep()}
      {step === 'validating' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'matching' && renderMatchingStep()}
      {step === 'manual' && renderManualMatchingStep()}
      {step === 'importing' && renderImportingStep()}
      {step === 'complete' && renderCompleteStep()}
    </Container>
  );
}

export default LetterboxdImport;
